/**
 * Regras da limpeza de capacidade das keys (desativar values sem tráfego no GAM).
 *
 * Puro de propósito, como o keyCapacity: é aqui que se decide quem vê o botão
 * e o que vai no POST, e um erro nisso desativa values no GAM de verdade.
 */
import type { KeyCapacityItem, KeyCleanupExample, KeyCleanupJob, KeyCleanupPreview } from '@/types/custom-targeting';

/** Abaixo disso o backend recusa; o botão nem aparece. */
export const CLEANUP_MIN_PERCENT = 50;
export const CLEANUP_DEFAULT_QUANTITY = 50000;
export const CLEANUP_MAX_QUANTITY = 100000;
export const CLEANUP_DEFAULT_IDLE_DAYS = 14;
/** Piso usado até a primeira prévia trazer o `min_idle_days` do backend. */
export const CLEANUP_FALLBACK_MIN_IDLE_DAYS = 7;
/** Intervalo de polling do job. */
export const CLEANUP_POLL_MS = 3000;
export const CLEANUP_EXAMPLES_SHOWN = 10;

/**
 * O botão aparece só para admin e só com a key em 50% ou mais dos ativos.
 * Esconder é conveniência: quem barra de verdade é o 403 do backend.
 */
export function canOfferCleanup(item: KeyCapacityItem | undefined, isAdmin: boolean): boolean {
    if (!isAdmin || !item || !item.exists) return false;
    const percent = item.percent;
    if (percent === null || percent === undefined || Number.isNaN(percent)) return false;
    return percent >= CLEANUP_MIN_PERCENT;
}

/** Converte o texto do campo em inteiro dentro de [min, max]; vazio/lixo cai no padrão. */
function clampInt(raw: string | number, min: number, max: number, fallback: number): number {
    const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/\D/g, ''));
    if (!Number.isFinite(n) || String(raw).trim() === '') return fallback;
    return Math.min(max, Math.max(min, Math.floor(n)));
}

export function clampQuantity(raw: string | number): number {
    return clampInt(raw, 1, CLEANUP_MAX_QUANTITY, CLEANUP_DEFAULT_QUANTITY);
}

export function clampIdleDays(raw: string | number, minIdleDays: number): number {
    // Sem teto definido pelo backend; um ano basta para não mandar absurdo.
    return clampInt(raw, minIdleDays, 365, Math.max(CLEANUP_DEFAULT_IDLE_DAYS, minIdleDays));
}

export function isHourKey(name: string): boolean {
    return name.endsWith('_hour');
}

export function isJobFinished(job: Pick<KeyCleanupJob, 'status'> | null | undefined): boolean {
    return !!job && job.status !== 'running';
}

/** Percentual desativado sobre o pedido, para a barra. Nunca passa de 100. */
export function jobProgress(job: Pick<KeyCleanupJob, 'deactivated' | 'quantity'>): number {
    if (!job.quantity || job.quantity <= 0) return 0;
    return Math.max(0, Math.min(100, (job.deactivated / job.quantity) * 100));
}

export const JOB_STATUS_LABEL: Record<KeyCleanupJob['status'], string> = {
    running: 'Em andamento',
    done: 'Concluída',
    failed: 'Falhou',
    interrupted: 'Interrompida',
};

/** Explica que regra a prévia aplicou, do jeito que o backend decide. */
export function cleanupRuleText(preview: Pick<KeyCleanupPreview, 'key' | 'idle_days' | 'report_up_to_date' | 'report_last_day'>): {
    regra: string;
    relatorio: string;
    soPorIdade: boolean;
} {
    const dias = `${preview.idle_days} ${preview.idle_days === 1 ? 'dia' : 'dias'}`;
    const ultimoDia = preview.report_last_day ? formatReportDay(preview.report_last_day) : 'sem dado';
    const soPorIdade = isHourKey(preview.key) && !preview.report_up_to_date;

    if (soPorIdade) {
        return {
            regra: `Sem tráfego há ${dias}.`,
            relatorio:
                `O relatório de tráfego do GAM não está em dia (último dia: ${ultimoDia}), então a seleção é ` +
                `só por idade: values criados há mais de ${dias}, sem confirmar pelo relatório que não tiveram tráfego.`,
            soPorIdade,
        };
    }
    return {
        regra: `Sem tráfego há ${dias}.`,
        relatorio: isHourKey(preview.key)
            ? `Relatório do GAM em dia (último dia: ${ultimoDia}).`
            : preview.report_up_to_date
                ? `Esta key exige o relatório do GAM em dia — está (último dia: ${ultimoDia}).`
                : `Esta key exige o relatório do GAM em dia, e ele não está (último dia: ${ultimoDia}).`,
        soPorIdade,
    };
}

/** "2026-04-02" → "02/04/2026", sem passar por Date (evita o deslocamento de fuso). */
export function formatReportDay(day: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : day;
}

/** Os mais antigos primeiro; sem data vão para o fim. */
export function oldestExamples(examples: KeyCleanupExample[], limit = CLEANUP_EXAMPLES_SHOWN): KeyCleanupExample[] {
    return [...examples]
        .sort((a, b) => {
            if (!a.processed_at) return b.processed_at ? 1 : 0;
            if (!b.processed_at) return -1;
            return a.processed_at < b.processed_at ? -1 : a.processed_at > b.processed_at ? 1 : 0;
        })
        .slice(0, limit);
}

/** A mensagem `detail` que o backend manda em 403/409/422, ou uma genérica. */
export function apiErrorDetail(error: unknown, fallback = 'Não foi possível concluir a operação.'): string {
    const e = error as { response?: { status?: number; data?: { detail?: unknown } }; message?: string } | null;
    const detail = e?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    // FastAPI devolve lista de erros de validação no 422 de schema.
    if (Array.isArray(detail) && detail.length > 0) {
        const msgs = detail
            .map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : ''))
            .filter(Boolean);
        if (msgs.length) return msgs.join('; ');
    }
    if (e?.response?.status === 403) return 'Só administradores podem liberar espaço nas keys.';
    return e?.message || fallback;
}
